import React from 'react';
import ResourceModal from './ResourceModal';
import { Separator } from "@/components/ui/separator";
import { FaNewspaper, FaExternalLinkAlt, FaBalanceScale, FaStar } from 'react-icons/fa';

interface FactCheckingComparisonProps {
  onClose: () => void;
}

const FactCheckingComparison: React.FC<FactCheckingComparisonProps> = ({ onClose }) => {
  return (
    <ResourceModal title="Fact-Checking Websites Comparison" onClose={onClose}>
      <div className="space-y-6">
        <div className="flex items-center gap-4 bg-purple-500/10 rounded-xl p-4">
          <div className="bg-purple-500/20 p-3 rounded-full">
            <FaNewspaper className="text-purple-400 h-8 w-8" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Article Overview</h3>
            <p className="text-gray-400">Overview of major fact-checking organizations and their methodologies</p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold mb-2">The Importance of Fact-Checking</h3>
          <p className="text-gray-300 mb-4">
            In an era of information overload, fact-checking organizations play a crucial role in verifying claims, correcting misinformation, 
            and helping the public navigate complex issues. These organizations have different approaches, areas of focus, and methodologies.
          </p>
          <p className="text-gray-300">
            Understanding how different fact-checkers operate can help you better evaluate their findings and choose which sources to rely on 
            for different types of information verification.
          </p>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-bold mb-4">Major Fact-Checking Organizations</h3>
          
          <div className="space-y-6">
            {[
              {
                name: "PolitiFact",
                organization: "Poynter Institute",
                founded: "2007",
                focus: "U.S. politics",
                methodology: "Uses a 'Truth-O-Meter' rating system with six levels from 'True' to 'Pants on Fire'",
                strengths: ["Thorough sourcing", "Clear methodology", "Independent ownership"],
                limitations: ["U.S. focus", "Political emphasis"],
                website: "politifact.com"
              },
              {
                name: "Snopes",
                organization: "Independent",
                founded: "1994",
                focus: "Urban legends, internet rumors, political claims",
                methodology: "Research-based fact-checking with rating system from 'True' to 'False' with nuanced options",
                strengths: ["Broad topic coverage", "Detailed research", "Pioneering fact-checker"],
                limitations: ["Smaller team", "Less standardized methodology than some others"],
                website: "snopes.com"
              },
              {
                name: "FactCheck.org",
                organization: "Annenberg Public Policy Center",
                founded: "2003",
                focus: "U.S. political claims and advertisements",
                methodology: "In-depth article format without simplified ratings, focusing on detailed analysis",
                strengths: ["Academic rigor", "Non-partisan approach", "Detailed explanations"],
                limitations: ["No rating system", "Slower publishing pace", "U.S. focus"],
                website: "factcheck.org"
              },
              {
                name: "Full Fact",
                organization: "UK-based charity",
                founded: "2010",
                focus: "UK politics, health, economy, and social issues",
                methodology: "In-depth research with emphasis on data analysis and expert consultation",
                strengths: ["Statistical expertise", "Automated fact-checking tools", "UK and European focus"],
                limitations: ["Less coverage of non-UK topics", "No simplified rating system"],
                website: "fullfact.org"
              },
              {
                name: "AFP Fact Check",
                organization: "Agence France-Presse",
                founded: "2017 (fact-checking division)",
                focus: "Global misinformation, with strong coverage in Europe, Asia, Latin America",
                methodology: "Multilingual fact-checking with clear verdicts and visual explanations",
                strengths: ["Global reach", "Multiple languages", "Visual misinformation expertise"],
                limitations: ["Varying depth depending on region", "Connected to news agency"],
                website: "factcheck.afp.com"
              }
            ].map((org, index) => (
              <div key={index} className="bg-black/30 border border-gray-800 rounded-lg overflow-hidden">
                <div className="bg-black/50 p-4 border-b border-gray-800">
                  <div className="flex justify-between items-start">
                    <h4 className="text-lg font-bold text-purple-400">{org.name}</h4>
                    <a href={`https://${org.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-gray-400 hover:text-purple-400 transition-colors">
                      {org.website} <FaExternalLinkAlt className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-gray-400">
                    <span>{org.organization}</span>
                    <span>Founded: {org.founded}</span>
                    <span>Focus: {org.focus}</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="mb-3">
                    <h5 className="font-medium mb-1 flex items-center gap-2">
                      <FaBalanceScale className="text-purple-400 h-4 w-4" /> Methodology
                    </h5>
                    <p className="text-gray-300 text-sm">{org.methodology}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium mb-1 flex items-center gap-2">
                        <FaStar className="text-green-400 h-4 w-4" /> Strengths
                      </h5>
                      <ul className="text-gray-300 text-sm space-y-1">
                        {org.strengths.map((strength, i) => (
                          <li key={i}>• {strength}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h5 className="font-medium mb-1 flex items-center gap-2">
                        <FaStar className="text-yellow-400 h-4 w-4" /> Limitations
                      </h5>
                      <ul className="text-gray-300 text-sm space-y-1">
                        {org.limitations.map((limitation, i) => (
                          <li key={i}>• {limitation}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-bold mb-4">Best Practices When Using Fact-Checkers</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                tip: "Use Multiple Sources",
                description: "Don't rely on a single fact-checker. Check multiple sources, especially for politically sensitive topics."
              },
              {
                tip: "Check Their Methodology",
                description: "Understand how each fact-checker conducts their verification and what standards they apply."
              },
              {
                tip: "Consider Funding Sources",
                description: "Be aware of who funds the fact-checker, as this could potentially influence their focus or approach."
              },
              {
                tip: "Look at Primary Sources",
                description: "Good fact-checkers cite their sources. Check those primary sources yourself when possible."
              },
              {
                tip: "Watch for Updates",
                description: "Fact-checkers sometimes update their verdicts as new information emerges. Check for the most recent analysis."
              },
              {
                tip: "Understand Regional Focus",
                description: "Use fact-checkers with expertise in the relevant geographical region for the claim being verified."
              }
            ].map((tip, index) => (
              <div key={index} className="bg-black/20 border border-gray-800 rounded-lg p-4">
                <h4 className="font-medium text-purple-400 mb-1">{tip.tip}</h4>
                <p className="text-gray-300 text-sm">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-black/30 border border-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-bold mb-2">Conclusion</h3>
          <p className="text-gray-300">
            Fact-checking organizations play a vital role in our information ecosystem, but they aren't infallible. 
            For the most accurate understanding of complex issues, use fact-checkers as one tool in your broader critical thinking toolkit. 
            Combine their findings with your own research, consultation of primary sources, and consideration of multiple perspectives.
          </p>
        </div>
      </div>
    </ResourceModal>
  );
};

export default FactCheckingComparison;
